// ============================================================================
// Overview — The Hive command center. A date-range selector drives a live
// aggregate of the existing endpoints (reports, suppression, campaigns, audit)
// plus UTM traffic/attribution (BigQuery via /api/traffic), a compliance
// "needs attention" panel, and the immutable audit activity feed.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'

const nf = (n) => Number(n || 0).toLocaleString('en-US')
const usd = (n) => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
const pct = (v) => (v == null ? '—' : (Math.round(Number(v) * 10) / 10) + '%')
function etDT(ts) {
  if (!ts) return '—'
  return new Date(Number(ts)).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const tile = (k, v, d, color) =>
  `<div class="stat"><div class="k">${esc(k)}</div><div class="v"${color ? ` style="color:${color}"` : ''}>${esc(v)}</div>${d ? `<div class="d">${esc(d)}</div>` : ''}</div>`

function utmTable(title, rows) {
  if (!rows || !rows.length) return ''
  const body = rows.map((r) => `
    <tr><td>${esc(r.key)}</td><td class="num">${nf(r.contracts)}</td><td class="num">${usd(r.funded)}</td></tr>`).join('')
  return `<div style="flex:1;min-width:240px">
    <div class="mod-sub" style="font-weight:800;text-transform:uppercase;letter-spacing:.4px;font-size:11px;margin:0 0 6px">${esc(title)}</div>
    <div class="table-wrap"><table class="h-table">
      <thead><tr><th>${esc(title)}</th><th>Funded</th><th>Revenue</th></tr></thead>
      <tbody>${body}</tbody></table></div>
  </div>`
}

export function renderOverview(el) {
  el.innerHTML = `
    <div class="card pad" style="margin-bottom:16px">
      <div class="toolbar">
        <div class="grow">
          <div class="mod-h">Command center</div>
          <div class="mod-sub">Messaging, deliverability, compliance &amp; attribution — live provider data.</div>
        </div>
        <label class="mod-sub" style="margin:0" for="ov-days">Range</label>
        <select class="h-input" id="ov-days" style="max-width:150px">
          <option value="7">Last 7 days</option>
          <option value="30" selected>Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last 12 months</option>
        </select>
      </div>
      <div class="stat-row" id="ov-stats" style="margin-top:14px"><div class="empty">Loading…</div></div>
    </div>

    <div class="card pad" style="margin-bottom:16px">
      <div class="mod-h">Messaging attribution <span class="pill an" style="vertical-align:middle">UTM · funded</span></div>
      <div class="mod-sub">Funded contracts &amp; revenue attributed to <b>your messaging</b> — Customer.io · SignalHouse · Hive blasts (BigQuery, by UTM).</div>
      <div id="ov-traffic" style="display:flex;gap:18px;flex-wrap:wrap;margin-top:12px"><div class="empty">Loading…</div></div>
    </div>

    <div class="grid-2" style="gap:16px">
      <div class="card pad">
        <div class="mod-h">Needs attention</div>
        <div class="mod-sub">Compliance &amp; deliverability signals.</div>
        <div id="ov-flags" style="margin-top:10px"><div class="empty">Loading…</div></div>
      </div>
      <div class="card pad">
        <div class="mod-h">Recent activity <span class="pill an" style="vertical-align:middle">Audit log</span></div>
        <div class="mod-sub">Every action, attributed and immutable.</div>
        <div class="table-wrap" style="margin-top:10px">
          <table class="h-table">
            <thead><tr><th>When</th><th>Action</th><th>Who</th><th>Target</th></tr></thead>
            <tbody id="ov-audit"><tr><td colspan="4" class="empty">Loading…</td></tr></tbody>
          </table>
        </div>
      </div>
    </div>`

  const $ = (id) => el.querySelector('#' + id)
  const daysSel = $('ov-days')

  function load() {
    const days = daysSel.value
    $('ov-stats').innerHTML = '<div class="empty">Loading…</div>'
    Promise.allSettled([
      api.get('/api/reports?days=' + days + '&account=all&logSize=10'),
      api.get('/api/suppression?limit=1'),
      api.get('/api/campaigns?limit=1'),
      api.get('/api/audit?limit=10'),
      api.get('/api/traffic?days=' + days),
    ]).then(([rep, sup, camp, aud, traf]) => {
      const r = rep.status === 'fulfilled' ? rep.value : {}
      const out = r.outbound || {}, inb = r.inbound || {}
      const suppTotal = sup.status === 'fulfilled' ? (sup.value.total || 0) : 0
      const campTotal = camp.status === 'fulfilled' ? (camp.value.total || 0) : 0
      const t = traf.status === 'fulfilled' ? traf.value : {}

      $('ov-stats').innerHTML =
        tile('Sent', nf(out.sent)) +
        tile('Delivered', nf(out.delivered), null, 'var(--ok)') +
        tile('Delivery rate', pct(out.deliveryRate)) +
        tile('Failed', nf(out.failed), null, out.failed ? 'var(--bad)' : null) +
        tile('Opt-outs', nf(inb.optOuts), pct(inb.optOutRate) + ' rate') +
        tile('On DNC list', nf(suppTotal), 'suppressed') +
        tile('Funded via msg', nf(t.totals && t.totals.contracts), t.totals ? usd(t.totals.funded) + ' revenue' : null) +
        tile('Campaigns', nf(campTotal))

      // ---- Traffic / UTM attribution ----
      $('ov-traffic').innerHTML =
        (utmTable('Channel', t.byChannel) + utmTable('UTM source', t.bySource) + utmTable('Campaign', t.byCampaign))
        || `<div class="empty">${esc(t.note || 'No attribution data for this range.')}</div>`

      // ---- Needs-attention flags ----
      const flags = []
      const dr = Number(out.deliveryRate)
      if (Number.isFinite(dr) && dr < 90) flags.push(['bad', `Delivery rate ${pct(out.deliveryRate)} — below 90%`])
      const oor = Number(inb.optOutRate)
      if (Number.isFinite(oor) && oor > 3) flags.push(['bad', `Opt-out rate ${pct(inb.optOutRate)} — above the 3% comfort line`])
      else if (Number.isFinite(oor) && oor > 1) flags.push(['warn', `Opt-out rate ${pct(inb.optOutRate)} — watch it`])
      for (const e of (r.errors || []).slice(0, 3)) {
        const mean = { '607': 'carrier spam block', '30007': 'carrier violation', '21610': 'sent to opted-out', '30003': 'unreachable handset' }[String(e.code)] || 'error'
        flags.push(['warn', `${nf(e.count)}× error ${esc(e.code)} (${mean})`])
      }
      $('ov-flags').innerHTML = flags.length
        ? flags.map(([lvl, x]) => `<div style="display:flex;gap:9px;align-items:center;padding:7px 0"><span class="badge ${lvl}">${lvl === 'bad' ? '!' : '•'}</span><span style="font-size:13.5px">${esc(x)}</span></div>`).join('')
        : '<div class="badge ok" style="margin-top:4px">All clear — no compliance or deliverability flags.</div>'

      // ---- Audit feed ----
      const entries = aud.status === 'fulfilled' ? (aud.value.entries || []) : []
      $('ov-audit').innerHTML = entries.length
        ? entries.map((e) => `<tr>
            <td style="white-space:nowrap">${esc(etDT(e.ts))}</td>
            <td><span class="pill">${esc(e.action)}</span></td>
            <td>${esc(e.actor_email || '—')}</td>
            <td style="max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(e.target || '')}</td>
          </tr>`).join('')
        : '<tr><td colspan="4" class="empty">No activity yet.</td></tr>'
    })
  }

  daysSel.addEventListener('change', load)
  load()
}
